import { memo, useMemo } from "react";

import { IconGitMerge, IconUserPlus } from "@tabler/icons";
import { useTheme } from "styled-components";

import { TableActions } from "@sso/components/TableActions";
import { Dialog } from "@sso/constant";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { openDialog } from "@sso/store/slices/dialog";
import {
  addOrganisationToMergeArray,
  removeOrganisationFromMergeArray,
  selectMergeOrganisations,
} from "@sso/store/slices/organisation";
import { Organisation } from "@sso/types";
import { Divider } from "@sso/ui/Divider";
import { IconButton } from "@sso/ui/IconButton";
import { TD } from "@sso/ui/Table";

interface OrganisationsTableActionsProps {
  org: Organisation;
}

export const OrganisationsTableActions = memo(({ org }: OrganisationsTableActionsProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const mergeOrgsArray = useAppSelector(selectMergeOrganisations);

  const { organisationId: id, name } = org;

  const isSelectedForMerge = useMemo(() => mergeOrgsArray.find(org => org.id === id), [mergeOrgsArray, id]);

  const handleRemoveOrganisation = () => {
    dispatch(openDialog({ type: Dialog.DELETE_ORGANISATION, id }));
  };

  const handleEditOrganisation = () => {
    dispatch(openDialog({ type: Dialog.EDIT_ORGANISATION, id }));
  };

  const handleOpenUserDialog = () => {
    dispatch(openDialog({ type: Dialog.ADD_USER, data: id }));
  };

  const handleMergeOrganisation = () => {
    dispatch(isSelectedForMerge ? removeOrganisationFromMergeArray(id) : addOrganisationToMergeArray({ id, name }));
  };

  return (
    <TD>
      <TableActions id={id} onEditClick={handleEditOrganisation} onRemoveClick={handleRemoveOrganisation}>
        <IconButton
          title={isSelectedForMerge ? "Remove from merging" : "Add for merging"}
          color={isSelectedForMerge ? theme.colors.warning.main : theme.colors.grey[400]}
          hoverColor={theme.colors.warning.light}
          onClick={handleMergeOrganisation}
        >
          <IconGitMerge />
        </IconButton>
        <IconButton
          title="Add User"
          color={theme.colors.grey[700]}
          hoverColor={theme.colors.success.main}
          onClick={handleOpenUserDialog}
        >
          <IconUserPlus />
        </IconButton>
        <Divider direction="v" height="20px" />
      </TableActions>
    </TD>
  );
});
